import { useState } from "react";
import { Menu, Home, User, LogOut, ShoppingCart, ChevronRight } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "wouter";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import logoMonochrome from "@/assets/universo-oro-logo-monochrome-optimized.png";
import { useCustomerAuth } from "@/contexts/customer-auth-context";
import { useLocalCart } from "@/contexts/local-cart-context";
import { QuotationStatusIndicator } from "./quotation-status-banner";
import type { QuotationStatus } from "@shared/schema";
import { useSettings } from "@/hooks/use-cms";
import { isPublicMenuItemActive, parsePublicMenuConfig, PUBLIC_MENU_SETTING_KEY } from "./menu-config";

interface MobileNavProps {
  goldPrice: number | null;
  silverPrice: number | null;
  quotationStatus?: QuotationStatus | null;
}

// Secondary pages
const secondaryItems = [
  { label: 'CHI SIAMO', href: '/chi-siamo' },
  { label: 'DOVE SIAMO', href: '/dove-siamo' },
  { label: 'CONTATTI', href: '/contatti' },
];

export function MobileNav({ goldPrice, silverPrice, quotationStatus }: MobileNavProps) {
  const [open, setOpen] = useState(false);
  const [location, setLocation] = useLocation();
  const { customer, isAuthenticated, logout } = useCustomerAuth();
  const { data: menuSettings } = useSettings([PUBLIC_MENU_SETTING_KEY]);
  const mainMenuItems = parsePublicMenuConfig(menuSettings?.[PUBLIC_MENU_SETTING_KEY]);
  
  // Local cart for guests
  const { totalItems: localCartItems } = useLocalCart();
  
  // Fetch cart for authenticated customers
  const { data: cartData } = useQuery<{ items: any[]; totalItems: number; totalAmount: number }>({
    queryKey: ["/api/cart"],
    enabled: isAuthenticated,
  });
  
  // Show local cart count for guests, server cart count for authenticated users
  const cartItemCount = isAuthenticated ? (cartData?.totalItems || 0) : localCartItems;

  const handleNavigate = (href: string) => {
    setLocation(href);
    setOpen(false);
  };
  
  const handleLogout = () => {
    logout();
    setLocation('/');
    setOpen(false);
  };

  const isActive = (href: string) => {
    return location === href || location.startsWith(href + '/');
  };

  return (
    <div className="flex items-center gap-2 md:hidden">
      {/* Cart Button for Mobile */}
      <Button
        variant="ghost"
        size="icon"
        className="relative text-white hover:text-uo-gold"
        onClick={() => {
          setLocation('/carrello');
        }}
        data-testid="mobile-cart-button"
        aria-label="Carrello"
      >
        <ShoppingCart className="h-5 w-5" />
        {cartItemCount > 0 && (
          <Badge 
            className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-primary text-primary-foreground"
            data-testid="mobile-cart-count"
          >
            {cartItemCount > 99 ? '99+' : cartItemCount}
          </Badge>
        )}
      </Button>
      
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:text-uo-gold"
            data-testid="mobile-menu-button"
          >
            <Menu className="h-6 w-6" />
            <span className="sr-only">Menu</span>
          </Button>
        </SheetTrigger>
        <SheetContent side="right" className="w-[min(340px,100vw)] max-w-full overflow-x-hidden overflow-y-auto bg-[hsl(var(--header-bg))] border-l border-uo-gold/30">
          {/* Header */}
          <SheetHeader className="mb-4 pb-4 border-b border-uo-gold/20">
            <div className="flex items-center gap-3">
              <img 
                src={logoMonochrome} 
                alt="UNIVERSO ORO" 
                className="h-8 w-auto"
                width={161}
                height={80}
              />
              <SheetTitle className="sr-only">Menu navigazione</SheetTitle>
            </div>
            
            {/* Compact Quotazioni */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs mt-3 px-1 min-w-0">
              {goldPrice != null && goldPrice > 0 && <div className="flex items-center gap-1 min-w-0">
                <span className="text-[#9c9e9f]">Oro 24k:</span>
                <span className="font-mono text-white font-medium">{goldPrice.toFixed(2).replace('.', ',')}€/Gr</span>
              </div>}
              {silverPrice != null && silverPrice > 0 && <div className="flex items-center gap-1 min-w-0">
                <span className="text-[#9c9e9f]">Argento:</span>
                <span className="font-mono text-white font-medium">{silverPrice.toFixed(2).replace('.', ',')}€/Gr</span>
              </div>}
              <QuotationStatusIndicator status={quotationStatus} testIdSuffix="mobile-nav" />
            </div>
          </SheetHeader>

          <div className="space-y-4">
            {/* Auth Section - Compact */}
            {isAuthenticated && customer ? (
              <div className="flex items-center justify-between p-3 rounded-md bg-uo-gold/10 border border-uo-gold/20">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-uo-gold flex items-center justify-center">
                    <User className="h-4 w-4 text-uo-dark" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{customer.firstName}</p>
                    <button 
                      onClick={() => handleNavigate('/area-clienti')}
                      className="text-xs text-uo-gold hover:underline"
                      data-testid="mobile-my-account"
                    >
                      Il mio account
                    </button>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-[#9c9e9f] hover:text-red-400"
                  onClick={handleLogout}
                  data-testid="mobile-logout"
                  aria-label="Logout"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button
                  className="flex-1 bg-uo-gold text-uo-dark hover:bg-uo-gold/90 text-sm font-medium"
                  onClick={() => handleNavigate('/accedi')}
                  data-testid="mobile-login"
                >
                  ACCEDI
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-uo-gold/50 text-uo-gold hover:bg-uo-gold/10 text-sm font-medium"
                  onClick={() => handleNavigate('/registrati')}
                  data-testid="mobile-register"
                >
                  REGISTRATI
                </Button>
              </div>
            )}

            <Separator className="bg-uo-gold/20" />

            {/* Main Navigation - Matching Desktop */}
            <nav className="space-y-1" aria-label="Navigazione mobile">
              {/* Home */}
              <Button
                variant={location === "/" ? "default" : "ghost"}
                className={`w-full justify-start text-sm font-medium uppercase tracking-wide ${
                  location === "/" 
                    ? 'bg-uo-gold text-uo-dark' 
                    : 'text-white hover:text-uo-gold hover:bg-uo-gold/10'
                }`}
                onClick={() => handleNavigate('/')}
                data-testid="mobile-nav-home"
              >
                <Home className="h-4 w-4 mr-2" />
                HOME
              </Button>

              {/* Main Menu Items - Services */}
              {mainMenuItems.map((item) => {
                const active = isPublicMenuItemActive(item, location);
                return (
                  <Button
                    key={item.href}
                    variant={active ? "default" : "ghost"}
                    className={`w-full justify-between text-sm font-medium uppercase tracking-wide ${
                      active
                        ? 'bg-uo-gold text-uo-dark' 
                        : 'text-white hover:text-uo-gold hover:bg-uo-gold/10'
                    }`}
                    onClick={() => handleNavigate(item.href)}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <span>{item.label}</span>
                    <ChevronRight className="h-4 w-4 opacity-50" />
                  </Button>
                );
              })}

              {/* Area Clienti - only when authenticated */}
              {isAuthenticated && (
                <Button
                  variant={location.startsWith('/area-clienti') ? "default" : "ghost"}
                  className={`w-full justify-between text-sm font-medium uppercase tracking-wide ${
                    location.startsWith('/area-clienti')
                      ? 'bg-uo-gold text-uo-dark' 
                      : 'text-white hover:text-uo-gold hover:bg-uo-gold/10'
                  }`}
                  onClick={() => handleNavigate('/area-clienti')}
                  data-testid="mobile-nav-area-clienti"
                >
                  <span>AREA CLIENTI</span>
                  <ChevronRight className="h-4 w-4 opacity-50" />
                </Button>
              )}
            </nav>

            <Separator className="bg-uo-gold/20" />

            {/* Secondary Navigation - Compact */}
            <div className="flex flex-wrap gap-2">
              {secondaryItems.map((item) => (
                <Button
                  key={item.href}
                  variant="ghost"
                  size="sm"
                  className={`text-xs ${
                    isActive(item.href)
                      ? 'text-uo-gold bg-uo-gold/10' 
                      : 'text-[#9c9e9f] hover:text-white hover:bg-uo-gold/5'
                  }`}
                  onClick={() => handleNavigate(item.href)}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.label}
                </Button>
              ))}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
